import clsx from "clsx";
import { Calendar } from "@/components/ui/calendar";
import React from "react";
import { MyCalendarEvent } from "@/modules/myCalendar/myCalendar.types";
import { MyCalendarList } from "@/modules/myCalendar/myCalendarList";
import { getDate, getMonth, getYear } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Helper function to get days grid data for a specific month and year
const getMonthGridData = (year: number, monthIndex: number) => {
  const firstDayOfMonth = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const startingDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7;

  const grid = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    grid.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    grid.push(i);
  }

  while (grid.length % 7 !== 0 || grid.length < 35) {
    // Ensure at least 5 full rows, or fill to 6 rows (42)
    grid.push(null);
    if (grid.length >= 42) break; // Max 6 rows
  }

  return grid;
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const dayNames = ["M", "T", "W", "T", "F", "S", "S"];
const currentYear = new Date().getFullYear();

interface Props {
  eventsMap: Map<string, Array<MyCalendarEvent>>;
}

export const MyCalendarCalendar: React.FC<Props> = ({ eventsMap }) => {
  const [selectedKeys, setSelectedKeys] = React.useState<Array<string>>([]);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Date>();
  const eventsToDisplay = selectedKey !== null ? (eventsMap.get(selectedKey) ?? []) : [];
  const [year, setYear] = React.useState(currentYear);

  return (
    <div>
      <div className="flex items-center justify-end">
        <Button variant="ghost" onClick={() => setYear(year - 1)}>
          <ChevronLeft className="text-blue-500" />
        </Button>
        <div className="mx-2">{year}</div>
        <Button variant="ghost" onClick={() => setYear(year + 1)}>
          <ChevronRight className="text-blue-500" />
        </Button>
      </div>
      <div className="mx-auto grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {monthNames.map((monthName, index) => {
          const date = new Date(year, index, 1);

          return (
            <div key={monthName}>
              <Calendar
                disableNavigation={true}
                className="!p-0"
                key={monthName}
                month={date}
                onSelect={value => {
                  if (!value) {
                    return;
                  }
                  const dayIndex = getDate(value);
                  const montIndex = getMonth(value) + 1;
                  const yearIndex = getYear(value);
                  const key = `${dayIndex}-${montIndex}-${yearIndex}`;

                  setSelected(value);
                  setSelectedKey(selectedKey === key ? null : key);
                }}
                selected={selected}
                mode="single"
                modifiers={{
                  hasEvents: [...eventsMap.values()].flat().map(item => new Date(item.datetime)),
                }}
                modifiersClassNames={{
                  hasEvents: "!bg-blue-50",
                  multipleEvents:
                    'after:content-["••"] after:block after:absolute after:bottom-0 after:translate-y-2 after:left-1/2 after:-translate-x-1/2 after:text-green-500 after:text-md',
                  selected: "!border-blue-200 text-white",
                  today: "!bg-blue-100",
                }}
              />
            </div>
          );
        })}
      </div>
      {selectedKey ? <MyCalendarList events={eventsToDisplay} /> : null}
    </div>
  );

  return (
    <div className="w-full">
      <div className="relative overflow-hidden rounded-xl border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <div className="mx-auto grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {monthNames.map((monthName, index) => {
              const monthGridData = getMonthGridData(year, index);

              return (
                <div
                  key={index}
                  className="rounded-lg border border-gray-200 bg-ui-card-background bg-white p-3 shadow-sm"
                >
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">{monthName}</h3>

                  <div className="mb-1 grid grid-cols-7 gap-1">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-sm font-bold text-gray-500">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {monthGridData.map((day, dayIndex) => {
                      const key = `${day}-${index + 1}-${year}`;

                      return (
                        <div key={`${dayIndex}-${index}-${year}`}>
                          <div
                            className={clsx(
                              "flex aspect-square items-center justify-center rounded-sm border border-gray-200 text-xs",
                              {
                                "border-1 border-blue-600 bg-blue-100": eventsMap.has(key),
                                "border-1 !border-blue-600": selectedKeys.includes(key),
                                "text-grey-800": day && day % 2 === 1,
                                "cursor-pointer": !!day,
                              },
                            )}
                            onClick={() => {
                              if (!day) {
                                return;
                              }
                              setSelectedKeys(
                                selectedKeys.includes(key)
                                  ? selectedKeys.filter(id => id !== key)
                                  : [...selectedKeys, key],
                              );
                            }}
                          >
                            {day}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {selectedKeys.length > 0 ? <MyCalendarList events={eventsToDisplay} /> : null}
        </div>
      </div>
    </div>
  );
};
